import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  ActionIcon,
  Menu,
  Skeleton,
  Image,
  SimpleGrid,
  Box,
  Card,
  Overlay,
  Center,
  Tooltip,
  ThemeIcon,
  Table,
  Tabs,
  Popover,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";

import { useForm } from "@mantine/form";
import { Dropzone, IMAGE_MIME_TYPE, FileWithPath } from "@mantine/dropzone";
import { DateTimePicker } from "@mantine/dates";
import {
  IconDotsVertical,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconUpload,
  IconPhoto,
  IconX,
  IconSearch,
  IconFilter,
  IconPencil,
  IconBrandYoutube,
  IconEye,
  IconEyeOff,
  IconSpeakerphone,
  IconCheck,
  IconClock,
  IconCalendar,
  IconSend,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import {
  PerformRequest,
  usePerformRequest,
  UploadFiles,
} from "../../../hooks/usePerformRequest";
import { HttpCodes } from "../../../lib/data";

type AnnouncementType = "youtube" | "image" | "carousel" | "text";

type Announcement = {
  _id: string;
  title: string;
  description?: string;
  type: AnnouncementType;
  mediaUrls: string[];
  thumbnail: string;
  isActive: boolean;
  createdAt: string;
};

const ConfirmationPopover = ({
  onConfirm,
  title,
  children,
  color = "red",
  confirmLabel = "Delete",
}: {
  onConfirm: () => void;
  title: string;
  children: React.ReactNode;
  color?: string;
  confirmLabel?: string;
}) => {
  const [opened, { close, open }] = useDisclosure(false);

  return (
    <Popover
      width={220}
      position="bottom"
      withArrow
      shadow="md"
      opened={opened}
      onClose={close}
    >
      <Popover.Target>
        <div onClick={open} style={{ cursor: "pointer" }}>
          {children}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {title}
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="light" size="compact-xs" color="gray" onClick={close}>
              Cancel
            </Button>
            <Button
              size="compact-xs"
              color={color}
              onClick={() => {
                onConfirm();
                close();
              }}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

const AnnouncementsPage = () => {
  useEffect(() => {
    document.title = "Announcements | Virel Admin";
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<FileWithPath[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<FileWithPath | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<string | null>(null); // "active", "inactive"

  const { data, isLoading, reloadData } = usePerformRequest<{
    data: Announcement[];
  }>({
    method: Endpoints.GetAnnouncements().method,
    route: Endpoints.GetAnnouncements().route,
  });

  const announcements = data?.data || [];

  const filteredAnnouncements = announcements.filter((item) => {
    const matchesSearch =
      !debouncedSearch ||
      item.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      item.description?.toLowerCase().includes(debouncedSearch.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" ? item.isActive : !item.isActive);

    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (id: string) => {
    const items = Endpoints.DeleteAnnouncement(id);

    await PerformRequest({
      method: items.method,
      route: items.route,
    });
    reloadData();
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    const items = Endpoints.ToggleAnnouncementStatus(id);
    await PerformRequest({
      method: items.method,
      route: items.route,
      data: { isActive: !currentStatus },
    });
    reloadData();
  };

  const form = useForm({
    initialValues: {
      title: "",
      description: "",
      type: "image" as AnnouncementType,
      mediaUrls: "", // multiline string
      thumbnail: "",
      isActive: true,
    },
    validate: {
      title: (v) => (!v.trim() ? "Title is required" : null),
      mediaUrls: (v, values) => {
        if (values.type === "youtube" && !v.trim())
          return "YouTube URL is required";
        if (values.type !== "text" && !v.trim() && pendingFiles.length === 0)
          return "Media is required for this type";
        return null;
      },
      thumbnail: (v, values) => {
        if (values.type === "youtube") return null;
        if (!v && !thumbnailFile) return "Thumbnail is required";
        return null;
      },
    },
  });

  const handleCreate = () => {
    setEditingId(null);
    form.reset();
    setPendingFiles([]);
    setThumbnailFile(null);
    setError(null);
    setModalOpen(true);
  };

  const handleEdit = (item: Announcement) => {
    setEditingId(item._id);
    form.setValues({
      title: item.title,
      description: item.description || "",
      type: item.type,
      mediaUrls: item.mediaUrls.join("\n"),
      thumbnail: item.thumbnail || "",
      isActive: item.isActive,
    });
    setPendingFiles([]);
    setThumbnailFile(null);
    setError(null);
    setModalOpen(true);
  };

  const handleDrop = async (files: FileWithPath[]) => {
    if (form.values.type === "image") {
      setPendingFiles(files.slice(0, 1));
    } else {
      setPendingFiles((prev) => [...prev, ...files]);
    }
  };

  const removeUrl = (urlOrIndex: string | number) => {
    if (typeof urlOrIndex === "number") {
      setPendingFiles((prev) => prev.filter((_, i) => i !== urlOrIndex));
    } else {
      const currentUrls = form.values.mediaUrls
        .split("\n")
        .map((u) => u.trim());
      const updated = currentUrls.filter((u) => u !== urlOrIndex).join("\n");
      form.setFieldValue("mediaUrls", updated);
    }
  };

  const submitForm = async () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    setIsSubmitting(true);
    setError(null);
    setIsUploading(true);

    let finalThumbnailUrl = form.values.thumbnail;
    let uploadedUrls: string[] = [];

    try {
      // 0. Upload thumbnail if new
      if (thumbnailFile) {
        const results = await UploadFiles([thumbnailFile]);
        const url = results[0]?.data?.data?.url;
        if (url) finalThumbnailUrl = url;
        else throw new Error("Thumbnail upload failed");
      }

      // 1. Upload pending media files if any
      if (pendingFiles.length > 0) {
        const results = await UploadFiles(pendingFiles);
        uploadedUrls = results
          .map((res) => res?.data?.data?.url)
          .filter((url) => url) as string[];
      }
    } catch (e) {
      console.error(e);
      setError("Failed to upload files. Please try again.");
      setIsSubmitting(false);
      setIsUploading(false);
      return;
    }

    // 2. Combine with existing URLs
    const existingUrls = form.values.mediaUrls
      .split("\n")
      .map((u) => u.trim())
      .filter((u) => u);

    let finalUrls: string[] = [];
    if (form.values.type === "image") {
      if (uploadedUrls.length > 0) {
        finalUrls = [uploadedUrls[0]];
      } else {
        finalUrls = existingUrls;
      }
    } else {
      finalUrls = [...existingUrls, ...uploadedUrls];
    }

    setIsUploading(false);

    if (
      finalUrls.length === 0 &&
      form.values.type !== "youtube" &&
      form.values.type !== "text"
    ) {
      setError("Please upload media content.");
      setIsSubmitting(false);
      return;
    }

    if (!finalThumbnailUrl) {
      if (form.values.type === "youtube" && finalUrls.length > 0) {
        // Auto-generate thumbnail from YouTube URL
        const videoUrl = finalUrls[0];
        let videoId = "";
        try {
          if (videoUrl.includes("v="))
            videoId = videoUrl.split("v=")[1].split("&")[0];
          else if (videoUrl.includes("youtu.be/"))
            videoId = videoUrl.split("youtu.be/")[1];
        } catch (e) {}

        if (videoId) {
          finalThumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        } else {
          // Fallback or error if video ID extraction fails?
          // For now, let's assume valid URL since validation passed, or default to standard generic thumb if needed,
          // but actually validation checked for "YouTube URL is required" if type is youtube.
          // If we can't get ID, maybe we shouldn't fail silently?
          // Let's stick to the extracted one.
        }
      }

      if (!finalThumbnailUrl) {
        setError("Thumbnail is required.");
        setIsSubmitting(false);
        return;
      }
    }

    const payload = {
      title: form.values.title,
      description: form.values.description,
      type: form.values.type,
      mediaUrls: finalUrls,
      thumbnail: finalThumbnailUrl,
      isActive: form.values.isActive,
    };

    let res;
    if (editingId) {
      const endpoint = Endpoints.UpdateAnnouncement(editingId);
      res = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: payload,
      });
    } else {
      const endpoint = Endpoints.CreateAnnouncement();
      res = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: payload,
      });
    }

    if (res.status === HttpCodes.Created || res.status === HttpCodes.Success) {
      setModalOpen(false);
      reloadData();
    } else {
      setError(res.message || "Failed to save announcement");
    }
    setIsSubmitting(false);
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "youtube":
        return (
          <Badge color="red" variant="light">
            YouTube
          </Badge>
        );
      case "image":
        return (
          <Badge color="green" variant="light">
            Image
          </Badge>
        );
      case "carousel":
        return (
          <Badge color="brand" variant="light">
            Carousel
          </Badge>
        );
      case "text":
        return (
          <Badge color="blue" variant="light">
            Text
          </Badge>
        );
      default:
        return <Badge color="gray">{type}</Badge>;
    }
  };

  const VideoThumbnail = ({ url }: { url: string }) => {
    // rudimentary youtube ID extraction
    let videoId = "";
    try {
      if (url.includes("v=")) videoId = url.split("v=")[1].split("&")[0];
      else if (url.includes("youtu.be/")) videoId = url.split("youtu.be/")[1];
    } catch (e) {}

    const thumb = videoId
      ? `https://img.youtube.com/vi/${videoId}/0.jpg`
      : null;

    return (
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          backgroundColor: "#000",
        }}
      >
        {thumb ? (
          <Image
            src={thumb}
            h="100%"
            w="100%"
            fit="cover"
            style={{ opacity: 0.7 }}
          />
        ) : (
          <Center h="100%">
            <IconBrandYoutube size={40} color="white" />
          </Center>
        )}
        <Center style={{ position: "absolute", inset: 0 }}>
          <IconBrandYoutube
            size={48}
            color="white"
            style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}
          />
        </Center>
      </div>
    );
  };

  // ... existing code ...
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushSegment, setPushSegment] = useState<string>("all");
  const [pushLoading, setPushLoading] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<Date | null>(null);
  const [recurrence, setRecurrence] = useState<string>("once");
  const [scheduledTab, setScheduledTab] = useState<string | null>("send");

  // Scheduled notifications state
  const [scheduledNotifications, setScheduledNotifications] = useState<any[]>(
    [],
  );
  const [loadingScheduled, setLoadingScheduled] = useState(false);

  // Fetch scheduled notifications
  const fetchScheduledNotifications = async () => {
    setLoadingScheduled(true);
    try {
      const ep = Endpoints.GetScheduledNotifications({ limit: 20 });
      const res = await PerformRequest<{ data: any[] }>({
        method: ep.method,
        route: ep.route,
      });
      if (res.status === HttpCodes.Success) {
        setScheduledNotifications(res.data?.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingScheduled(false);
    }
  };

  useEffect(() => {
    if (pushModalOpen) {
      fetchScheduledNotifications();
    }
  }, [pushModalOpen]);

  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      setError("Title and Body are required.");
      return;
    }

    if (isScheduled && !scheduledFor) {
      setError("Please select a scheduled time.");
      return;
    }

    if (isScheduled && scheduledFor && scheduledFor <= new Date()) {
      setError("Scheduled time must be in the future.");
      return;
    }

    setPushLoading(true);
    setError(null);
    try {
      if (isScheduled && scheduledFor) {
        // Schedule the notification
        const ep = Endpoints.CreateScheduledNotification();
        const res = await PerformRequest({
          method: ep.method,
          route: ep.route,
          data: {
            title: pushTitle,
            body: pushBody,
            segment: pushSegment,
            scheduledFor: scheduledFor.toISOString(),
            recurrence: recurrence,
          },
        });
        if (
          res.status === HttpCodes.Created ||
          res.status === HttpCodes.Success
        ) {
          setPushModalOpen(false);
          setPushTitle("");
          setPushBody("");
          setScheduledFor(null);
          setIsScheduled(false);
          setRecurrence("once");
          const recurrenceText =
            recurrence === "daily" ? " (recurring daily)" : "";
          setSuccessMessage(
            `Notification scheduled for ${scheduledFor.toLocaleString()}${recurrenceText}!`,
          );
          setSuccessModalOpen(true);
        } else {
          setError(res.message || "Failed to schedule notification");
        }
      } else {
        // Send immediately
        const ep = Endpoints.BroadcastPush();
        const res = await PerformRequest({
          method: ep.method,
          route: ep.route,
          data: { title: pushTitle, body: pushBody, segment: pushSegment },
        });
        if (res.status === HttpCodes.Success) {
          setPushModalOpen(false);
          setPushTitle("");
          setPushBody("");
          setSuccessMessage("Notification sent successfully!");
          setSuccessModalOpen(true);
        } else {
          setError(res.message || "Failed to send notification");
        }
      }
    } catch (e) {
      setError("An error occurred.");
    } finally {
      setPushLoading(false);
    }
  };

  const handleCancelScheduled = async (id: string) => {
    try {

      const ep = Endpoints.CancelScheduledNotification(id);
      const res = await PerformRequest({
        method: ep.method,
        route: ep.route,
      });
      if (res.status === HttpCodes.Success) {
        fetchScheduledNotifications();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteScheduled = async (id: string) => {
    try {

      const ep = Endpoints.DeleteScheduledNotification(id);
      const res = await PerformRequest({
        method: ep.method,
        route: ep.route,
      });
      if (res.status === HttpCodes.Success) {
        fetchScheduledNotifications();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2} fw={700}>
              Announcements
            </Title>
            <Text c="dimmed" size="sm">
              Manage marketing and notification cards
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              color="green"
              onClick={() => {
                setPushTitle("");
                setPushBody("");
                setError(null);
                setPushModalOpen(true);
              }}
            >
              Send Push Notification
            </Button>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={handleCreate}
              size="sm"
              color="black"
              radius="md"
            >
              Create New
            </Button>
          </Group>
        </Group>

        <Group>
          <TextInput
            placeholder="Search announcements..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
            radius="md"
          />
          <Select
            placeholder="Status"
            data={[
              { value: "active", label: "Active Only" },
              { value: "inactive", label: "Inactive Only" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            style={{ width: 180 }}
            leftSection={<IconFilter size={16} />}
            radius="md"
          />
          <ActionIcon
            size="lg"
            variant="default"
            radius="md"
            onClick={() => reloadData()}
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={300} radius="md" />
            ))}
          </SimpleGrid>
        ) : filteredAnnouncements.length === 0 ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="var(--bg-surface-2)">
            <Text c="dimmed">
              No announcements found matching your criteria.
            </Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
            {filteredAnnouncements.map((item) => (
              <Card
                key={item._id}
                padding="md"
                radius="md"
                withBorder
                shadow="sm"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Card.Section
                  h={160}
                  bg="var(--bg-surface-2)"
                  style={{ position: "relative" }}
                >
                  {item.thumbnail ? (
                    <Image src={item.thumbnail} h={160} w="100%" fit="cover" />
                  ) : (
                    <Center h="100%" bg="var(--bg-surface-2)">
                      <IconPhoto size={40} color="gray" />
                    </Center>
                  )}
                  <Badge
                    pos="absolute"
                    top={10}
                    right={10}
                    color={item.isActive ? "green" : "gray"}
                    variant="filled"
                    size="sm"
                  >
                    {item.isActive ? "Active" : "Inactive"}
                  </Badge>
                </Card.Section>

                <Stack mt="md" gap="xs" style={{ flex: 1 }}>
                  <Group justify="space-between" align="start" wrap="nowrap">
                    <Text fw={600} lineClamp={1} title={item.title}>
                      {item.title}
                    </Text>
                    {getTypeBadge(item.type)}
                  </Group>

                  <Text size="xs" c="dimmed" lineClamp={3} h={48}>
                    {item.description || "No description provided."}
                  </Text>
                </Stack>

                <Group mt="md" gap="sm" grow>
                  <Button
                    variant="light"
                    color={item.isActive ? "gray" : "green"}
                    size="xs"
                    leftSection={
                      item.isActive ? (
                        <IconEyeOff size={14} />
                      ) : (
                        <IconEye size={14} />
                      )
                    }
                    onClick={() => handleToggleStatus(item._id, item.isActive)}
                  >
                    {item.isActive ? "Hide" : "Publish"}
                  </Button>
                  <ActionIcon.Group>
                    <Tooltip label="Edit">
                      <ActionIcon
                        variant="light"
                        color="green"
                        size="md"
                        onClick={() => handleEdit(item)}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <ConfirmationPopover
                      title="Delete this announcement?"
                      onConfirm={() => handleDelete(item._id)}
                    >
                      <Tooltip label="Delete">
                        <ActionIcon variant="light" color="red" size="md">
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </ConfirmationPopover>

                  </ActionIcon.Group>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* Push Notification Modal */}
      <Modal
        opened={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        title={
          <Group gap="xs">
            <ThemeIcon variant="light" color="green" size="lg" radius="md">
              <IconSpeakerphone size={20} />
            </ThemeIcon>
            <Text fw={700}>Push Notifications</Text>
          </Group>
        }
        centered
        radius="md"
        size="lg"
        padding="xl"
      >
        <Tabs value={scheduledTab} onChange={setScheduledTab}>
          <Tabs.List mb="md">
            <Tabs.Tab value="send" leftSection={<IconSend size={16} />}>
              Send Notification
            </Tabs.Tab>
            <Tabs.Tab
              value="scheduled"
              leftSection={<IconCalendar size={16} />}
            >
              Scheduled (
              {
                scheduledNotifications.filter((n) => n.status === "pending")
                  .length
              }
              )
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="send">
            <Stack gap="md">
              {error && pushModalOpen && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconX size={16} />}
                  title="Error"
                >
                  {error}
                </Alert>
              )}

              <Alert
                variant="light"
                color="green"
                title="About Broadcasts"
                icon={<IconFilter size={16} />}
              >
                <Text size="sm">
                  This will send a push notification to <b>ALL active users</b>{" "}
                  who have enabled notifications. When users tap this
                  notification, it will simply open the application.
                </Text>
              </Alert>

              <Stack gap="xs">
                <TextInput
                  label="Notification Title"
                  description="Keep it short and catchy"
                  placeholder="e.g. New Features Available!"
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.currentTarget.value)}
                  required
                  radius="md"
                />
                <Select
                  label="Target Audience"
                  description="Who should receive this notification?"
                  data={[
                    { value: "all", label: "All Users" },
                    { value: "active", label: "Active Users (Last 30 days)" },
                    { value: "inactive", label: "Inactive Users" },
                    { value: "monetized", label: "Monetized Users Only" },
                  ]}
                  value={pushSegment}
                  onChange={(val) => setPushSegment(val || "all")}
                  allowDeselect={false}
                  radius="md"
                />
                <Textarea
                  label="Notification Message"
                  description="The main body of your alert"
                  placeholder="e.g. Check out the latest updates to your dashboard..."
                  value={pushBody}
                  onChange={(e) => setPushBody(e.currentTarget.value)}
                  minRows={3}
                  required
                  radius="md"
                />
              </Stack>

              <Switch
                label="Schedule for later"
                description="Send this notification at a specific time"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.currentTarget.checked)}
                mt="sm"
              />

              {isScheduled && (
                <DateTimePicker
                  label="Scheduled Time"
                  description="Select when to send this notification (Nigerian time)"
                  placeholder="Pick date and time"
                  value={scheduledFor}
                  onChange={(value) => {
                    if (value === null) {
                      setScheduledFor(null);
                    } else if (typeof value === "string") {
                      setScheduledFor(new Date(value));
                    } else {
                      setScheduledFor(value);
                    }
                  }}
                  minDate={new Date()}
                  required
                  radius="md"
                />
              )}

              {isScheduled && (
                <Radio.Group
                  label="Frequency"
                  description="How often should this notification be sent?"
                  value={recurrence}
                  onChange={setRecurrence}
                >
                  <Group mt="xs">
                    <Radio value="once" label="Just once" />
                    <Radio value="daily" label="Recurring daily" />
                  </Group>
                </Radio.Group>
              )}

              <Group justify="flex-end" mt="md">
                <Button
                  variant="default"
                  onClick={() => setPushModalOpen(false)}
                  radius="md"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendPush}
                  loading={pushLoading}
                  color="green"
                  radius="md"
                  leftSection={
                    isScheduled ? (
                      <IconClock size={16} />
                    ) : (
                      <IconSend size={16} />
                    )
                  }
                >
                  {isScheduled ? "Schedule Notification" : "Send Now"}
                </Button>
              </Group>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scheduled">
            <Stack gap="md">
              {loadingScheduled ? (
                <Stack gap="xs">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} height={60} radius="md" />
                  ))}
                </Stack>
              ) : scheduledNotifications.length === 0 ? (
                <Paper p="xl" withBorder radius="md" ta="center" bg="var(--bg-surface-2)">
                  <Text c="dimmed">No scheduled notifications</Text>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {scheduledNotifications.map((notification) => (
                    <Paper key={notification._id} p="md" withBorder radius="md">
                      <Group justify="space-between" align="flex-start">
                        <div style={{ flex: 1 }}>
                          <Group gap="xs" mb={4}>
                            <Text fw={600} size="sm" lineClamp={1}>
                              {notification.title}
                            </Text>
                            <Badge
                              size="xs"
                              color={
                                notification.status === "pending"
                                  ? "blue"
                                  : notification.status === "sent"
                                    ? "green"
                                    : notification.status === "cancelled"
                                      ? "gray"
                                      : "red"
                              }
                            >
                              {notification.status}
                            </Badge>
                            {notification.recurrence === "daily" && (
                              <Badge size="xs" color="violet" variant="light">
                                recurring
                              </Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {notification.body}
                          </Text>
                          <Group gap="xs" mt={6}>
                            <IconClock size={12} color="gray" />
                            <Text size="xs" c="dimmed">
                              {new Date(
                                notification.scheduledFor,
                              ).toLocaleString()}
                            </Text>
                            <Badge size="xs" variant="light">
                              {notification.segment}
                            </Badge>
                            {notification.totalSendCount > 0 && (
                              <Text size="xs" c="dimmed">
                                (sent {notification.totalSendCount}x)
                              </Text>
                            )}
                          </Group>
                        </div>
                        {notification.status === "pending" && (
                          <ActionIcon.Group>
                            <ConfirmationPopover
                              title="Cancel this notification?"
                              onConfirm={() =>
                                handleCancelScheduled(notification._id)
                              }
                              color="orange"
                              confirmLabel="Cancel"
                            >
                              <Tooltip label="Cancel">
                                <ActionIcon
                                  variant="light"
                                  color="orange"
                                  size="sm"
                                >
                                  <IconX size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </ConfirmationPopover>

                            <ConfirmationPopover
                              title="Delete this notification?"
                              onConfirm={() =>
                                handleDeleteScheduled(notification._id)
                              }
                            >
                              <Tooltip label="Delete">
                                <ActionIcon variant="light" color="red" size="sm">
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </ConfirmationPopover>
                          </ActionIcon.Group>
                        )}
                        {notification.status !== "pending" && (
                          <ConfirmationPopover
                            title="Delete this notification?"
                            onConfirm={() =>
                              handleDeleteScheduled(notification._id)
                            }
                          >
                            <Tooltip label="Delete">
                              <ActionIcon variant="light" color="red" size="sm">
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </ConfirmationPopover>
                        )}

                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchScheduledNotifications}
                loading={loadingScheduled}
              >
                Refresh
              </Button>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Modal>

      {/* Existing Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? "Edit Announcement" : "Create Announcement"}
        size="lg"
        centered
      >
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" icon={<IconX size={16} />}>
              {error}
            </Alert>
          )}

          <TextInput
            label="Title"
            placeholder="Enter announcement title"
            {...form.getInputProps("title")}
            withAsterisk
          />

          <Textarea
            label="Description"
            placeholder="Enter a brief description (optional)"
            autosize
            minRows={2}
            {...form.getInputProps("description")}
          />

          <Select
            label="Display Type"
            data={[
              { value: "image", label: "Single Image Banner" },
              { value: "carousel", label: "Image Carousel" },
              { value: "youtube", label: "YouTube Video Embed" },
              { value: "text", label: "Text Announcement" },
            ]}
            {...form.getInputProps("type")}
          />

          <Switch
            label="Set as Active"
            description="If enabled, this announcement will be visible to users immediately."
            {...form.getInputProps("isActive", { type: "checkbox" })}
          />

          <Paper p="md" radius="md" withBorder bg="var(--bg-surface-2)">
            <Text size="sm" fw={500} mb="sm">
              Thumbnail Result{" "}
              {form.values.type === "youtube"
                ? "(Auto-generated)"
                : "(Required)"}
            </Text>
            <Dropzone
              onDrop={(files) => setThumbnailFile(files[0])}
              onReject={() =>
                setError("File rejected. Ensure it is an image and under 3MB.")
              }
              maxSize={3 * 1024 ** 2}
              accept={IMAGE_MIME_TYPE}
              maxFiles={1}
              loading={isUploading}
              radius="md"
            >
              <Group
                justify="center"
                gap="md"
                mih={80}
                style={{ pointerEvents: "none" }}
              >
                <Dropzone.Idle>
                  <IconPhoto size={40} color="var(--mantine-color-dimmed)" />
                </Dropzone.Idle>
                <Dropzone.Accept>
                  <IconUpload size={40} color="var(--mantine-color-green-6)" />
                </Dropzone.Accept>
                <Box>
                  <Text size="sm" fw={500} ta="center">
                    Drag thumbnail here
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Max 3MB
                  </Text>
                </Box>
              </Group>
            </Dropzone>
            {thumbnailFile ? (
              <Paper
                mt="xs"
                pos="relative"
                withBorder
                radius="sm"
                style={{ overflow: "hidden", display: "inline-block" }}
              >
                <Image
                  src={URL.createObjectURL(thumbnailFile)}
                  h={100}
                  w="auto"
                  fit="cover"
                />
                <ActionIcon
                  color="red"
                  variant="filled"
                  size="xs"
                  pos="absolute"
                  top={2}
                  right={2}
                  onClick={() => setThumbnailFile(null)}
                >
                  <IconX size={10} />
                </ActionIcon>
              </Paper>
            ) : form.values.thumbnail ? (
              <Paper
                mt="xs"
                pos="relative"
                withBorder
                radius="sm"
                style={{ overflow: "hidden", display: "inline-block" }}
              >
                <Image
                  src={form.values.thumbnail}
                  h={100}
                  w="auto"
                  fit="cover"
                />
                <ActionIcon
                  color="red"
                  variant="filled"
                  size="xs"
                  pos="absolute"
                  top={2}
                  right={2}
                  onClick={() => form.setFieldValue("thumbnail", "")}
                >
                  <IconX size={10} />
                </ActionIcon>
              </Paper>
            ) : null}
          </Paper>

          <Paper p="md" radius="md" withBorder bg="var(--bg-surface-2)">
            <Text size="sm" fw={500} mb="sm">
              Media Content {form.values.type === "text" && "(Optional)"}
            </Text>
            {form.values.type === "youtube" ? (
              <TextInput
                placeholder="https://youtube.com/watch?v=..."
                leftSection={<IconBrandYoutube size={16} />}
                {...form.getInputProps("mediaUrls")}
              />
            ) : (
              <Stack>
                <Dropzone
                  onDrop={handleDrop}
                  onReject={() =>
                    setError(
                      "File rejected. Ensure it is an image and under 3MB.",
                    )
                  }
                  maxSize={3 * 1024 ** 2}
                  accept={IMAGE_MIME_TYPE}
                  multiple={form.values.type === "carousel"}
                  maxFiles={form.values.type === "image" ? 1 : 10}
                  loading={isUploading}
                  radius="md"
                >
                  <Group
                    justify="center"
                    gap="md"
                    mih={80}
                    style={{ pointerEvents: "none" }}
                  >
                    <Dropzone.Idle>
                      <IconPhoto
                        size={40}
                        color="var(--mantine-color-dimmed)"
                      />
                    </Dropzone.Idle>
                    <Dropzone.Accept>
                      <IconUpload
                        size={40}
                        color="var(--mantine-color-green-6)"
                      />
                    </Dropzone.Accept>
                    <Box>
                      <Text size="sm" fw={500} ta="center">
                        Drag images here or click to select
                      </Text>
                      <Text size="xs" c="dimmed" ta="center">
                        Max 3MB per file.
                      </Text>
                    </Box>
                  </Group>
                </Dropzone>

                <SimpleGrid cols={4} mt="xs">
                  {/* Existing URLs */}
                  {form.values.mediaUrls
                    .split("\n")
                    .filter((u: string) => u.trim())
                    .map((url: string, index: number) => (
                      <Paper
                        key={`existing-${index}`}
                        pos="relative"
                        withBorder
                        radius="sm"
                        style={{ overflow: "hidden" }}
                      >
                        <Image src={url} h={60} w="100%" fit="cover" />
                        <ActionIcon
                          color="red"
                          variant="filled"
                          size="xs"
                          pos="absolute"
                          top={2}
                          right={2}
                          onClick={() => removeUrl(url)}
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Paper>
                    ))}

                  {/* Pending Files */}
                  {pendingFiles.map((file, index) => {
                    const imageUrl = URL.createObjectURL(file);
                    return (
                      <Paper
                        key={`pending-${index}`}
                        pos="relative"
                        withBorder
                        radius="sm"
                        style={{ overflow: "hidden" }}
                      >
                        <Image
                          src={imageUrl}
                          h={60}
                          w="100%"
                          fit="cover"
                          opacity={0.8}
                        />
                        <Badge
                          size="xs"
                          pos="absolute"
                          bottom={0}
                          left={0}
                          fullWidth
                          radius={0}
                        >
                          New
                        </Badge>
                        <ActionIcon
                          color="red"
                          variant="filled"
                          size="xs"
                          pos="absolute"
                          top={2}
                          right={2}
                          onClick={() => removeUrl(index)}
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Stack>
            )}
          </Paper>

          <Button onClick={submitForm} loading={isSubmitting} fullWidth mt="md">
            {editingId ? "Save Changes" : "Create Announcement"}
          </Button>
        </Stack>
      </Modal>

      {/* Success Modal */}
      <Modal
        opened={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title={
          <Group gap="xs">
            <IconCheck size={20} color="green" />
            <Text fw={700} c="green">
              Success
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{successMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setSuccessModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default AnnouncementsPage;
